import { Component, Fragment, ReactNode } from 'react';

import { IconHelper } from '../../../utils/icon-helper';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import Tooltip from '../tooltip/tooltip.component';
import style from './multi-level-dropdown.shared.style.module.scss';

export interface MultiLevelDropdownItem {
    isHeader?: boolean;
    hasOptions?: boolean;
    selected?: boolean;
    value: any | any[];
}

// export class MultiLevelDropdownHeader<T> {
//     item: T;

//     constructor(
//         item: T,
//     ) {
//         this.item = item;
//     }
// }

// export class MultiLevelDropdownOption<T> {
//     item: T;

//     constructor(
//         item: T,
//     ) {
//         this.item = item;
//     }
// }

// export class MultiLevelDropdownSection<H, O> {
//     header: MultiLevelDropdownHeader<H>;
//     options: MultiLevelDropdownOption<O>[];

//     constructor(
//         header: MultiLevelDropdownHeader<H>,
//         options: MultiLevelDropdownOption<O>[],
//     ) {
//         this.header = header;
//         this.options = options;
//     }
// }



export interface MultiLevelDropdownProps<T extends MultiLevelDropdownItem> {
    children?: any;
    values: T[];
    valueFormatter: (value: T) => string;
    hasTooltip: (value: T) => boolean;
    getTooltip: (value: T) => string;
    selectedValueFormatter?: (value: T) => string;
    initialValue?: T | undefined;
    placeholder?: string;
    label?: string;
    btnClassNames?: string;
    btnGroupClassNames?: string;
    firstBtnStyles?: any;
    onSelect?: (selectedValue: T, forced?: boolean) => void
    canSelect?: (selectedValue: T) => Promise<boolean>
    shownFn?: (value: T) => boolean;
    emitInitialValueOnInit?: boolean;
    getOptions(value: any): any[]
}

interface MultiLevelDropdownState<T> {
    values: T[];
    selectedValue?: T | undefined;
    shown?: boolean;
}

export default class MultiLevelDropdown<T extends MultiLevelDropdownItem> extends Component<MultiLevelDropdownProps<T>, MultiLevelDropdownState<T>> {
    static getDerivedStateFromProps(props, state) {
        return {
            selectedValue: MultiLevelDropdown.getSelectedValue(props.values),
            values: props.values,
        }
    }

    static getSelectedValue(values: any) {
        if (values == null || !Array.isArray(values) || values.length == 0) {
            return null
        }

        const selectedItem = values.find(i => i.selected);
        if (selectedItem) {
            return selectedItem;
        }
        const parentItems = values.filter(i => i.hasOptions);
        for (const item of parentItems) {
            const selectedItem = this.getSelectedValue(item.value);
            if (selectedItem) {
                return selectedItem;
            }
        }
    }

    static unselectAllSelectableItems(values: any) {
        if (values == null || !Array.isArray(values) || values.length == 0) {
            return null
        }

        const selectedItem = values.filter(i => !i.hasOptions).forEach(i => i.selected = false);

        const parentItems = values.filter(i => i.hasOptions);
        return parentItems.forEach(i => this.unselectAllSelectableItems(i.value));
    }

    static updateSelectedValue(values: any, selectedValue: any) {
        MultiLevelDropdown.unselectAllSelectableItems(values);
        selectedValue.selected = true;
    }

    wrapperRef: any;
    btnClassNames = 'btn btn-light btn-light-outline';
    firstBtnStyles = {};

    readonly state: Readonly<MultiLevelDropdownState<T>> = {
        selectedValue: this.props.initialValue,
        values: this.props.values,
        shown: false,
    };

    constructor(props: MultiLevelDropdownProps<T>) {
        super(props);
        this.firstBtnStyles = this.props.firstBtnStyles;
        this.handleClickOutside = this.handleClickOutside.bind(this);

    }

    componentDidMount() {
        if (this.props.emitInitialValueOnInit) {
            this.updateSelectedValue(this.props.initialValue);
        }
    }

    setWrapperRef(node: any) {
        this.wrapperRef = node;
    }

    handleClickOutside(event: any) {
        if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
            this.toggleDropdown();
        }
    }

    toggleDropdown() {
        if (!this.state.shown) {
            document.addEventListener('mousedown', this.handleClickOutside, { passive: true });
        } else {
            document.removeEventListener('mousedown', this.handleClickOutside);
        }
        this.setState({
            shown: !this.state.shown,
        });
    }

    async userTapedOnDropdownItem(selectedValue: T) {
        if (this.props.canSelect) {
            if (!await this.props.canSelect(selectedValue)) {
                return;
            }
        }
        this.updateSelectedValue(selectedValue);
        this.toggleDropdown();
    }

    updateSelectedValue(selectedValue: T, forced = false) {
        MultiLevelDropdown.updateSelectedValue(this.state.values, selectedValue);
        this.setState({
            selectedValue: selectedValue,
        });
        if (this.props.onSelect) {
            this.props.onSelect(selectedValue, forced);
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.valueFilter(this.state.selectedValue)) {
            this.updateSelectedValue(this.props.values.find((v) => this.valueFilter(v)), true);
        }
    }

    

    private valueFilter(value: T) {
        return !this.props.shownFn || this.props.shownFn(value);
    }

    renderHeader(value: T) {
        return <div>
            {
                this.props.valueFormatter(value)
            }
        </div>
    }

    renderOptions(items: any[]) {
        return items.map((val, index) => {
            return (
                <li key={`${index}`} className={`${this.props.valueFormatter(val) === this.props.valueFormatter(this.state.selectedValue) ? style.active : ''}`}
                    onClick={() => this.userTapedOnDropdownItem(val)}>{this.props.valueFormatter(val)}
                    <span className={style.tooltip}>
                        {
                            this.props.hasTooltip(val) &&
                            <Tooltip text={this.props.getTooltip(val)}>
                                {
                                    IconHelper.getIconElement(HtmlEntityTypes.INFO_WITH_CIRCLE)
                                }
                            </Tooltip>
                        }
                    </span>
                </li>
            )
        })

    }

    formatSelectedValue(selectedValue: any) {
        if (this.props.selectedValueFormatter) {
            return this.props.selectedValueFormatter(selectedValue)
        }
        if (this.props.valueFormatter) {
            return this.props.valueFormatter(selectedValue)
        }
        return this.props.placeholder || 'Not Selected'
    }

    render(): ReactNode {

        return (
            <div ref={(node) => this.setWrapperRef(node)} className={`${style.dropdown} ${this.props.btnGroupClassNames || ''}`}>
                {
                    <div key="dropdownTrigger" className={`x-btn-group ${style['dropdown-btn-group']}`} onClick={() => this.toggleDropdown()}>
                        {
                            this.props.label && <button type="button" className={`${this.btnClassNames} ${style['label']}`} disabled>{this.props.label}
                            </button>
                        }
                        <button type="button" className={`${this.btnClassNames} ${style['value']}`} style={this.firstBtnStyles}>
                            {this.formatSelectedValue(this.state.selectedValue)}
                        </button>
                        <button type="button" className={`${this.btnClassNames} ${style['caret']}`}>&#9660;</button>
                    </div>
                }
                <ul key="dropdownMenu" className={`${style["dropdown-content"]} ${this.state.shown ? style.show : ''}`}>
                    {
                        this.state.shown && this.state.values
                            .filter(val => this.valueFilter(val))
                            .map((val, index) => {
                                return <div key={`multi-level-item-${index}`}>
                                    {
                                        this.renderHeader(val)
                                    }
                                    {
                                        this.renderOptions(val.value)
                                    }
                                </div>
                            })
                    }
                </ul>
            </div>
        );
    }
}